import {
    type Point,
    type TripFilters,
    type TripShareInfo,
    TripSortBy,
} from './TripModuleTypes';
import { type TaxiLateralLoader } from '@itinerary/trip/bus/TaxiLateralLoader';
import type Trip from '@itinerary/trip/Trip';
import type TripPackage from '@itinerary/trip/TripPackage';
import type TripPackageSet from '@itinerary/trip/TripPackageSet';
import type TripPriceClass from '@itinerary/trip/TripPriceClass';
import {
    type EmptyListReason,
    LoadingStatus,
    type SortBy,
} from '@itinerary/ItineraryModuleTypes';
import ItineraryModule from '@itinerary/ItineraryModule';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { type Location } from '@utils/types';
import { ReserveType } from '@utils/itinerary';
import { TripType, type TripUrlParams } from '@utils/trip';
import { type TripPackageLoaderConfig } from '@itinerary/trip/types';

export default abstract class TripModule<
    TTrip extends Trip = Trip,
    TPrice extends TripPriceClass = TripPriceClass,
    TFilters extends TripFilters = TripFilters,
    TLocation extends Location<TLocation> = Location,
    TPackage extends TripPackage<TTrip, TPrice> = TripPackage<TTrip, TPrice>,
> extends ItineraryModule<TPrice, TFilters, TLocation, TPackage> {
    //#region state

    /**
     * Indicates the variable to sort trip packages by
     *
     * @type {TripSortBy}
     * @memberof TripModule
     */
    sortBy: TripSortBy = TripSortBy.Price;

    /**
     * List of available trip packages
     *
     * @type {{[key: string]: {normal: TripPackage<TTrip, TPrice>[];promoted: TripPackage<TTrip, TPrice>[];};}}
     * @memberof TripModule
     */
    itineraryPackageSets: (TripPackageSet<
        TFilters['meta'],
        TFilters['key'],
        TPackage
    > | null)[] = [];
    // never assign to any value
    //#endregion

    override get emptyListReason(): EmptyListReason {
        return {
            image: `/img/no-result/${this.reserveInfo.type}.svg`,
            title: 'در تاریخ و مسیر مورد نظر',
            notFoundText: `${this.reserveInfo.nameE} موجود نیست!`,
        };
    }

    override get haveTracking(): boolean {
        // if trip have pre sell always have tracking
        if (this.hasPreSell) {
            return true;
        }
        return (
            this.currentLoadingStatus === LoadingStatus.Success &&
            this.areAllPackagesFull &&
            !this.reservableListWithoutLaterals.length
        );
    }

    private get searchOrigin(): Location | null {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        return currentSearchForm.value.origin;
    }

    private get searchDestination(): Location | null {
        const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
        return currentSearchForm.value.destination;
    }

    get originName(): string {
        let originName =
            this.currentPackageSet?.originName ?? this.searchOrigin?.title;
        originName = originName?.replace(/(.+) - (?:پایانه )?(.+)/, '$1 ($2)');
        return originName ?? '';
    }

    get destinationName(): string {
        let destinationName =
            this.currentPackageSet?.destinationName ??
            this.searchDestination?.title;
        destinationName = destinationName?.replace(
            /(.+) - (?:پایانه )?(.+)/,
            '$1 ($2)'
        );
        return destinationName ?? '';
    }

    get originCurrentCode(): string {
        const originCode =
            this.currentPackageSet?.originCode ?? this.searchOrigin?.code;
        return originCode ?? '';
    }

    get destinationCurrentCode(): string {
        const destinationCode =
            this.currentPackageSet?.destinationCode ??
            this.searchDestination?.code;
        return destinationCode ?? '';
    }

    override get mainTitle() {
        if (!this.originName || !this.destinationName) {
            return '';
        }
        return `بلیط  ${this.reserveInfo.title} ${this.originName.replace(
            '(همه فرودگاه‌ها)',
            ''
        )} به ${this.destinationName.replace('(همه فرودگاه‌ها)', '')}`;
    }

    override get dateTitle() {
        const { departureDate, returnDate } =
            useCurrentSearchForm<TripSearchForm>().value;
        const currentDate = this.currentSetIndex ? returnDate : departureDate;
        if (!currentDate) {
            return '';
        }
        return dayConfig
            .js(currentDate)
            .format(formatter({ format: 'long', includeWeekday: true }));
    }

    override get sortByList(): SortBy[] {
        return [
            {
                type: TripSortBy.Time,
                label: 'زود‌ترین زمان',
            },
            {
                type: TripSortBy.Price,
                label: 'کمترین قیمت',
            },
        ];
    }

    override applySort(list: TripPackage[]): TripPackage[] {
        return list.concat().sort((a, b) => {
            if (a.pinToTop && b.pinToTop) {
                // do nothing, continue to normal sort below
            } else if (a.pinToTop) {
                return -1;
            } else if (b.pinToTop) {
                return 1;
            }
            if (this.isInitialSort) {
                if (a.isSpecialOffer && b.isSpecialOffer) {
                    // do nothing, continue sort below
                }
                if (a.isSpecialOffer) {
                    return -1;
                }
                if (b.isSpecialOffer) {
                    return 1;
                }
            }
            const defaultSort =
                a.order[this.sortBy] - b.order[this.sortBy] ||
                (a.order.customOrder &&
                    b.order.customOrder &&
                    a.order.customOrder(a) - b.order.customOrder(b)) ||
                0;
            if (defaultSort) {
                return defaultSort;
            }
            const alternativeSorts = EnumClass.create(TripSortBy)
                .getValues()
                .filter(sort => sort !== this.sortBy)
                .filter(sort =>
                    this.sortByList.map(sort => sort.type).includes(sort)
                );
            for (const sort of alternativeSorts) {
                const sortResult = a.order[sort] - b.order[sort];
                if (sortResult) {
                    return sortResult;
                }
            }
            return 0;
        });
    }

    override get sortedReservableList(): TripPackage[] {
        return this.applySort(this.list.filter(pck => pck.reservable));
    }

    override get sortedNonReservableList(): TripPackage[] {
        return this.applySort(this.list.filter(pck => !pck.reservable));
    }

    get originLocation(): Point | null {
        return this.currentPackageSet?.originLocation || null;
    }

    get destinationLocation(): Point | null {
        return this.currentPackageSet?.destinationLocation || null;
    }

    abstract override getPackageSet(
        loaderConfig: TripPackageLoaderConfig
    ): TripPackageSet<TFilters['meta'], TFilters['key'], TPackage>;

    get lateralLoader(): TaxiLateralLoader | null {
        return null;
    }

    abstract override getInitialFilters(): { [key in TFilters['key']]: any };

    override handleFirstDateChange(date: Date | null) {
        const currentIndex = useItineraryStore().currentSetIndex;
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        if (!this.itineraryPackageSets[currentIndex] || !date) {
            return;
        }
        if (currentIndex > 0) {
            currentSearchForm.returnDate = date;
        } else {
            if (
                this.itineraryPackageSets[1] &&
                dayConfig.gregory(date).isAfter(currentSearchForm.returnDate)
            ) {
                currentSearchForm.returnDate = date;
            }
            currentSearchForm.departureDate = date;
        }
    }

    get averagePrice(): number {
        const searchFormStore = useSearchFormStore();
        const currentVehicle =
            searchFormStore.reserveType === ReserveType.Flight
                ? 'plane'
                : searchFormStore.reserveType;
        const list = this.list.filter(
            p => p.vehicleType === currentVehicle && p.reservable
        );
        return (
            Math.round(
                list.reduce(
                    (prev, curr) =>
                        prev + (curr.defaultPrice?.singlePayable ?? 0),
                    0
                ) /
                    list.filter(p => p.defaultPrice?.singlePayable).length /
                    10000
            ) * 10000
        );
    }

    override get list(): TripPackage[] {
        if (this.isSelectingReturn) {
            const previousSelectedPackage = usePackageSelectStore()
                .selectedPackages[0] as TripPackage;
            const { arrivalDateTime } = previousSelectedPackage;
            return (super.list as TripPackage[]).filter(
                res =>
                    res.departureDateTime.getTime() > arrivalDateTime.getTime()
            );
        }
        return super.list as TripPackage[];
    }

    get routeCountInfo(): TripShareInfo | null {
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        if (!this.originLocation || !this.destinationLocation) {
            return null;
        }
        return {
            origin: this.originLocation,
            destination: this.destinationLocation,
            departureDate: currentSearchForm.departureDate,
            returnDate: currentSearchForm.returnDate,
            adultCount: currentSearchForm.adultCount,
            childCount: currentSearchForm.childCount,
            infantCount: currentSearchForm.infantCount,
            expectedPackagesToBeSelected:
                currentSearchForm.tripType === TripType.OneWay ? 1 : 2,
            tripType: currentSearchForm.tripType,
            isSelectingReturn: this.isSelectingReturn,
        };
    }

    get currentSetIndex(): number {
        return useItineraryStore().currentSetIndex;
    }

    override get currentPackageSet():
        | TripPackageSet<TFilters['meta'], TFilters['key']>
        | null
        | undefined {
        return this.itineraryPackageSets[this.currentSetIndex];
    }

    override get currentItineraryPackages() {
        return this.currentPackageSet?.list || null;
    }

    override get emptyPackage(): TripPackage {
        throw new Error('Not implemented!');
    }

    override get expectedPackagesToBeSelected(): number {
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        return currentSearchForm.tripType === TripType.OneWay ? 1 : 2;
    }

    override get description(): string | null {
        return this.descriptionTemplate
            .replace(/\(مبدا\)/g, this.originName)
            .replace(/\(مقصد\)/g, this.destinationName);
    }

    override get canonicalRegex() {
        return /(.*?)\/?\d{4}-\d{2}-\d{2}.+$/;
    }

    abstract override getItineraryPackageLoaderConfigs(
        params: TripUrlParams
    ): TripPackageLoaderConfig[];

    override async loadAvailable(params: {
        index: number;
        inForm?: boolean;
    }): Promise<void> {
        await super.loadAvailable(params);
        const currentSearchForm =
            useSearchFormStore().getFormModule<TripSearchForm>(
                this.reserveInfo.type
            );
        if (currentSearchForm.departureDate && !params.inForm) {
            currentSearchForm.loadPrices({
                until: dayConfig
                    .gregory(currentSearchForm.departureDate)
                    .add(7, 'day')
                    .startOf('day')
                    .toDate(),
            });
        }
    }

    override async loadLaterals() {
        if (
            !this.currentPackageSet?.date ||
            !this.currentPackageSet?.originCode ||
            !this.currentPackageSet?.destinationCode
        ) {
            return;
        }
        try {
            const currentSearchForm =
                useSearchFormStore().getFormModule<TripSearchForm>(
                    this.reserveInfo.type
                );
            const result = await this.lateralLoader?.loadItineraries({
                originCode: this.currentPackageSet.originCode,
                destinationCode: this.currentPackageSet.destinationCode,
                date: this.currentPackageSet.date,
                count: currentSearchForm.adultCount,
            });
            this.lateralPackage =
                result?.slice().sort((a, b) => {
                    if (
                        a.departureDateTime.getTime() ===
                        b.departureDateTime.getTime()
                    ) {
                        return a.singlePayable - b.singlePayable;
                    }
                    return (
                        a.departureDateTime.getTime() -
                        b.departureDateTime.getTime()
                    );
                })?.[0] || null;
        } catch (e) {
            reportException(e);
        }
    }
}
